<template>
  <CModal :auto-focus="false" show :to="attachTo" :trap-focus="false" :z-index="zIndex" @keydown.esc="saveAndClose">
    <div
      class="flex flex-col pa-4 gap-4 rounded-t-4 bg-primary6 self-end ma-0 w-full h-[calc(100vh-40px)] slide-in-animation"
    >
      <AppHeader @cancel="cancel()" @close="saveAndClose()" />

      <main class="flex flex-auto flex-col gap-4 overflow-auto">
        <template v-if="isInitialized">
          <CollectionToolbar />

          <VueDraggable
            class="flex flex-auto flex-col bg-white rounded-2 pa-4 gap-6 overflow-auto"
            :disabled="isDragDropDisabled"
            handle="[data-handle-group]"
            :model-value="collection.groups"
            v-bind="DragConfig"
            @update:model-value="updateGroups"
          >
            <CollectionGroup v-for="group in collection.groups" :key="group.id" :group="group" />
          </VueDraggable>
        </template>

        <div
          v-else
          class="border-style-solid border-primary4 border-width-1 rounded-2 pa-4 justify-center flex items-center grow-1"
        >
          <CSpin :color="themeVars.primary" size="large" />
        </div>
      </main>

      <AppFooter :isLoading="isSaving" @cancel="cancel()" @close="saveAndClose()" />
    </div>
  </CModal>

  <MoveSlideModal
    v-if="moveSlideModal.isOpen.value"
    :groups="collection.groups"
    :z-index="childModalZIndex"
    @moveToGroup="moveSlideToGroup"
  />
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import CModal from '@lib/components/CModal/CModal.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import type { CollectionPlayerWithPages } from '@ppt-conversion-selector/types/model.type'
import AppHeader from '@ppt-conversion-selector/components/AppHeader.vue'
import AppFooter from '@ppt-conversion-selector/components/AppFooter.vue'
import CollectionToolbar from '@ppt-conversion-selector/components/CollectionToolbar.vue'
import { type Api, provideAppStore } from '@ppt-conversion-selector/stores/app.store'
import { provideTableInput } from '@ppt-conversion-selector/composables/table-input.use'
import CollectionGroup from '@ppt-conversion-selector/components/CollectionGroup.vue'
import MoveSlideModal from '@ppt-conversion-selector/components/modals/MoveSlideModal.vue'
import { provideMoveSlideModal } from '@ppt-conversion-selector/composables/move-slide-modal.use'
import { DragConfig } from '@ppt-conversion-selector/constants'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import useThemeVars from '@lib/composables/useThemeVars'

export type Emit = {
  close: [CollectionPlayerWithPages]
  cancel: []
  contentChange: [CollectionPlayerWithPages]
}

const props = defineProps<{
  zIndex?: number
  attachTo?: string | HTMLElement
  initialData: CollectionPlayerWithPages
  instanceId: string
  fileTags?: string[]
  api: Api
}>()

const emit = defineEmits<Emit>()

provideTableInput()
const confirmation = useConfirmation()
const { t } = useI18n()
const moveSlideModal = provideMoveSlideModal()
const themeVars = useThemeVars()

const {
  collection,
  moveSlideToGroup,
  updateGroups,
  childModalZIndex,
  isInitialized,
  initialize,
  isSaving,
  saveAndClose,
  isDragDropDisabled,
} = provideAppStore({
  initialCollection: props.initialData,
  t,
  confirmation,
  api: props.api,
  emit,
  zIndex: props.zIndex,
  instanceId: props.instanceId,
  fileTags: props.fileTags,
})

const cancel = () => emit('cancel')

initialize()
</script>

<style lang="scss" scoped>
:deep(.n-collapse-item__header-main) {
  font-size: 15px;
  line-height: 18px !important;
  letter-spacing: 0.15px;
  padding: 8px !important;

  .n-collapse-item-arrow {
    margin-left: 0 !important;
    font-size: 12px !important;
    margin-right: 8px !important;
  }
}

@keyframes slideInFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.slide-in-animation {
  animation: slideInFromBottom 0.2s ease-out forwards;
}
</style>
